import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle2, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reset password confirm — reads ?token=... from URL, POSTs to backend.
 * Never validates the token client-side; the server owns single-use +
 * expiry logic.
 */
export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) setError("This reset link is missing a token. Please request a new one.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw1.length < 6) return setError("Password must be at least 6 characters.");
    if (pw1 !== pw2) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f8] grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#e6edf1] shadow-sm p-6 sm:p-8" data-testid="reset-password-page">
        <button
          onClick={() => nav("/login")}
          className="inline-flex items-center gap-1 text-xs text-[#457b9d] hover:text-[#1d3557] mb-6"
          data-testid="rp-back"
        >
          <ArrowLeft size={14} /> Back to login
        </button>

        {done ? (
          <>
            <div className="w-12 h-12 rounded-full bg-[#22c55e]/15 text-[#16a34a] grid place-items-center mb-4">
              <CheckCircle2 size={22} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557] mb-2" data-testid="rp-success">
              Password updated
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed">
              Your Fender password has been changed. You can now log in with
              your new password.
            </p>
            <Button
              onClick={() => nav("/login")}
              className="w-full mt-6 bg-[#e63946] hover:bg-[#d02f3c] text-white"
              data-testid="rp-goto-login"
            >
              Log in
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-[22px] font-bold text-[#1d3557] mb-1" data-testid="rp-title">
              Choose a new password
            </h1>
            <p className="text-sm text-[#667085] mb-6">
              Enter a new password for your Fender account. It must be at
              least 6 characters long.
            </p>

            {error && (
              <div className="flex items-start gap-2 p-3 mb-4 rounded-md bg-[#e63946]/8 border border-[#e63946]/20 text-[#e63946]" data-testid="rp-error">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="text-xs leading-relaxed">{error}</div>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="rp-pw1" className="text-xs uppercase tracking-wider text-[#457b9d]">New password</Label>
                <div className="mt-1 relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    id="rp-pw1"
                    type={show ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    className="pl-9 pr-10"
                    minLength={6}
                    required
                    data-testid="rp-pw1"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98A2B3] hover:text-[#457b9d]" tabIndex={-1}>
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="rp-pw2" className="text-xs uppercase tracking-wider text-[#457b9d]">Confirm new password</Label>
                <div className="mt-1 relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    id="rp-pw2"
                    type={show ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="pl-9"
                    minLength={6}
                    required
                    data-testid="rp-pw2"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-[#e63946] hover:bg-[#d02f3c] text-white"
                data-testid="rp-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
              </Button>
            </form>

            <p className="text-xs text-[#667085] mt-6 leading-relaxed">
              Link expired? <Link to="/forgot-password" className="text-[#e63946] font-semibold">Request a new one</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
